import { ViewportScroller } from '@angular/common';
import { AfterViewInit, Directive, inject, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import cloneDeep from 'lodash/cloneDeep';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, takeUntil } from 'rxjs';
import { v4 as uuid } from 'uuid';

import {
  assignSafe,
  FileContentType,
  IFileContainer,
  IFileReference,
  isString,
  SBaseDisplay,
  SDate
} from '@summit/core';
import {
  BatchChange,
  DateRange,
  DisplaySchema,
  DisplayType,
  isNumber,
  ISummitGroupEvent,
  ISummitItemEvent,
  IUserExperience,
  SummitIdObject,
  SummitUserExperienceService
} from '@summit/models';
import {
  CrudCacheItem,
  FormFunctions,
  HistoryService,
  SummitCrudService,
  SummitNotificationService
} from '@summit/services';
import { ApiError } from '@summit/ui';

import { SFormInputFileComponent } from '../components/s-form-input-file/s-form-input-file.component';
import { SummitDialogDirective } from '../directives';
import { DialogEditConfig } from './dialog-edit-config';
import { IGridMin } from './i-grid-min';
import { SummitChildConfigs } from './summit-child-edit-configs';
import { SummitFormBase } from './summit-form-base';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const testCreationMode = false;

@Directive({
  providers: [SummitCrudService, ActivatedRoute],
  standalone: false
})
export abstract class SummitEditPrime<T extends SummitIdObject>
  extends SummitFormBase
  implements OnInit, OnDestroy, AfterViewInit
{
  // Abstract Properties
  element?: T;
  abstract schema: DisplaySchema<T>;
  childEditConfigs?: SummitChildConfigs<T>;
  protected savingString = 'Saving...';

  // Base Properties
  debug = false;
  @Input() newEntry = false;
  pseudoCard = false;
  draft = false;
  id: string | number | null;
  loadingBs: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean> = this.loadingBs.asObservable();
  loaded$: Observable<boolean> = this.loading$.pipe(map((loading) => !loading));
  get loaded(): boolean {
    return !this.loadingBs.value;
  }
  loadingDialogRef?: DynamicDialogRef;
  viewportScroller: ViewportScroller;
  defaultViewName: string;
  protected router: Router;
  protected draftId?: string;
  protected formBuilder: FormBuilder;
  protected crud: SummitCrudService;
  protected activeRoute: ActivatedRoute;
  protected history: HistoryService;
  protected uxService: SummitUserExperienceService<IUserExperience>;
  protected notificationService: SummitNotificationService;
  // protected notificationPrimeService: NotificationPrimeService;
  //When the component is in a dialog
  protected dialogConfig?: DynamicDialogConfig<DialogEditConfig<T>>;
  protected inDialog: boolean;
  protected configData?: DialogEditConfig<T>;
  protected dialogRef?: DynamicDialogRef;
  protected hiddenFields: (keyof T)[] = [];
  private initialized: boolean;

  // Debouncing for updateChildren to prevent excessive calls during initialization
  private updateChildrenTimer: any = null;
  protected updateChildrenDebounceMs = 10; // Configurable debounce delay, can be adjusted based on performance needs

  //Files Component
  fileUploadComponentName?: string;

  sDialog = new SummitDialogDirective();

  //Grid Min References
  @ViewChildren('sfGrid') gridChildren?: QueryList<IGridMin<SummitIdObject>>;

  constructor() {
    ///protected uxService?: UserExperienceService //protected dialog?: MatDialog, //protected history?: HistoryService, //protected route: ActivatedRoute, //protected crud: CrudService, //protected formBuilder: FormBuilder,
    super();
    this.initialized = false;
    this.id = null;
    this.defaultViewName = '';
    this.formBuilder = inject(FormBuilder);
    this.router = inject(Router);
    this.crud = inject(SummitCrudService);
    this.activeRoute = inject(ActivatedRoute);
    this.history = inject(HistoryService);
    this.uxService = inject(SummitUserExperienceService);
    this.viewportScroller = inject(ViewportScroller);
    this.notificationService = inject(SummitNotificationService);
    // this.notificationPrimeService = inject(NotificationPrimeService);
    try {
      this.dialogConfig = inject(DynamicDialogConfig);
      this.dialogRef = inject(DynamicDialogRef);
    } catch (e) {
      //NOP - Just a full screen component
    }
    this.inDialog = this.dialogConfig ? true : false;

    //console.log('[route]',this.route)
    if (this.inDialog) {
      this.configData = this.dialogConfig?.data as DialogEditConfig<T>;
      //console.log('SummitEditPrime: Dialog Config', this.configData);
      if (this.configData?.element?.id && this.configData.loadObject) this.id = this.configData.element.id;
      //console.log('SummitEditPrime: Dialog Config', this.configData);
    } else if (this.activeRoute) {
      this.configData = {} as DialogEditConfig<T>;
      this.activeRoute.queryParamMap.pipe(takeUntil(this.disposing$)).subscribe((paramMap: ParamMap) => {
        this.defaultViewName = paramMap.get('view') ?? '';
        const draftId = paramMap.get('draftId');
        if (draftId) {
          //console.log('[Draft Id found]', draftId);
          this.id = draftId;
          this.draft = true;
        }
      });
      this.activeRoute.paramMap.pipe(takeUntil(this.disposing$)).subscribe((paramMap: ParamMap) => {
        //console.log('params', paramMap);
        paramMap.keys.length !== 0 ? (this.pseudoCard = true) : (this.pseudoCard = false);
        //console.log(`Component Received `, params);
        const newId = paramMap.get('id');
        const draftId = paramMap.get('draftId');
        if ((this.id || this.id === 0) && (newId !== this.id || newId === draftId)) {
          //console.log(`[EboEdit] Resetting to ${newId}`);
          this.id = newId ?? null;
          this.ngOnInit(); // reset and set based on new parameter this time
        }
      });
    } else {
      console.error(`No ActivatedRoute was provided to Summit-Edit`);
    }
  }
  openSaveToast() {
    this.notificationService?.saved();
    // this.notificationService.triggerToast('Update Available', null, 'info', null, null, null, true);
  }

  ngOnInit() {
    this.initialized = true;
    if (this.inDialog) {
      //console.log(`[SummitEdit] Dialog Config: `, this.dialogConfig);
      this.newEntry = this.dialogConfig?.data?.newEntry || !this.dialogConfig?.data?.element;
      this.element = this.schema.NewObject(this.dialogConfig?.data?.element, this.uxService.getUserExperience());
      //console.log(`[SummitEdit] Dialog Config: Element:`, this.element);
      this.hiddenFields = this.dialogConfig?.data?.hideFields ?? [];
      //console.log(this.hiddenFields);
    }
    if (this.debug) console.log(`EBOEdit: ngOnInit`);
    combineLatest([this.loading$, this.submitting$])
      .pipe(takeUntil(this.disposing$))
      .subscribe(([loading, submitting]) => {
        //console.log(`loading: ${this.loading$.value}, submitted: ${this.submitting$.value}`);
        if (loading) {
          // Display Loading text
          //console.log(`Loading...`, this.loading$.value || this.submitting$.value);
          this.showProgressSpinner(loading || submitting);
        } else {
          this.showProgressSpinner(loading || submitting, this.savingString);
        }
      });
    if (this.debug) console.log(`Updating Loading to ${true}`);
    this.loadingBs.next(true);
    // ** Allow the child class to override the ID before we test it.
    if (!this.id && this.id !== 0) {
      this.id = this.activeRoute?.snapshot.paramMap.get('id') ?? null;
    }
    //console.log(`Got ${this.id} from Route:`, this.activeRoute.snapshot.paramMap);
    if (this.id === 'create' || this.newEntry) {
      this.id = null;
      this.newEntry = true;
      this.element ??= this.schema?.NewObject();
      this.processUrlParameters(this.activeRoute?.snapshot.queryParamMap);
    }

    const formGroupParameters = new Object();
    /*
    console.log(
      `Processing id ${this.id}: New Entry: ${this.newEntry}, Draft Entry: ${this.draft}`,
      this.schema,
      this.hiddenFields
    );
    */
    FormFunctions.BuildFormGroup(this.formBuilder, formGroupParameters, this.schema, this, this.hiddenFields);
    this.elementForm = this.formBuilder.group(formGroupParameters);
    //console.log(`FormGroup`, this.elementForm, this.schema, this.hiddenFields);
    this.configureForm();
    //console.log(`elementForm:`, this.elementForm);
    //console.log(`[SummitEdit] Form Built`, this.draft, this.newEntry, this.id);
    if (this.draft) {
      //console.log(`Fetching Draft Item ${this.id}`);
      const draftItem = this.id != null ? (this.crud.getDraft(this.id) as CrudCacheItem<T>) : null;
      //console.log(`Results from Draft ${this.id}`, draftItem);
      if (draftItem) {
        this.processCrudResult(draftItem);
        //console.log(`Loaded Draft Item`, this.element);
      }
      this.updateForm();
      this.UpdateTitle();
      //console.log(`Setting Loading to ${false}`);
      this.loadingBs.next(false);
    } else if (!this.newEntry && (this.id || this.id === 0)) {
      this.crud
        .get(this.schema, this.id.toString())
        .pipe(takeUntil(this.disposing$))
        .subscribe(async (result) => {
          if (this.debug) console.log('got result:', result);
          if (testCreationMode)
            console.log(`Test Creation Server Response:\n ${JSON.stringify(result?.data, null, 2)}`);
          const resultItem = result as CrudCacheItem<T>;
          if (this.debug) console.log(`Updating Loading to ${resultItem?.parameters?.isLoading}`, result);
          if (!resultItem?.parameters?.isLoading) this.loadingBs.next(false);
          try {
            this.processCrudResult(resultItem);
          } catch (e) {
            console.error(e);
          }
          //console.log(`Updating form based on crud`);
          this.updateForm();
          this.UpdateTitle();
        });
      // console.log('Updated with ', this.element, this.elementForm);
    } else {
      if (this.debug)
        console.log(`Updating form based on current element \n${JSON.stringify(this.element, null, 2)}`, this.element);
      this.updateForm();
      this.UpdateTitle();
      //console.log(`Setting Loading to false`);
      this.loadingBs.next(false);
    }
  }

  ngAfterViewInit() {
    this.updateGridRefs();
    this.updateChildren();
    console.log('[SummitEditPrime] ngAfterViewInit - After updateChildren');

    // Enable validators after initialization completes
    // Components that override ngAfterViewInit with async initialization should call finishInitialization() themselves
    setTimeout(() => {
      if (this.isInitializing) {
        this.finishInitialization();
        console.log('[SummitEditPrime] ngAfterViewInit - Initialization complete (base class), validators enabled');
      }
    }, 0);
  }

  updateGridRefs() {
    const childEditConfigs = this.childEditConfigs;
    if (childEditConfigs) {
      this.gridChildren?.forEach((child) => {
        const childConfig = childEditConfigs[child.fieldName];
        if (childConfig) {
          childConfig.gridRef = child;
        } else {
          console.error(`No childEditConfig for ${String(child.fieldName)} in`, childEditConfigs);
        }
      });
      const missingGridRefs = Object.entries(childEditConfigs)
        .filter(([, config]) => config != null && !config.gridRef)
        .map(([key]) => key);
      if (missingGridRefs.length > 0) {
        console.error(`Missing Grid Refs for ${missingGridRefs.join(',')}`, childEditConfigs);
      }
      //console.log(`Updated Grid Refs`, this.gridChildren, this.childEditConfigs);
    }
  }

  public processUrlParameters(paramMap: ParamMap | undefined) {
    if (this.debug) {
      console.log(`Parameters`, paramMap);
    }
    if (paramMap && this.schema) {
      //console.log('schema', this.schema);
      const formFieldList = this.schema.getFormAttributeList().map((entry) => entry.propertyName);
      // console.log(`static object fields: ${formFieldList.join(',')}`);
      //*noUnusedParameters var jsonData = {};
      paramMap.keys.forEach((value) => {
        //console.log(`Received ${_key} = ${value} searching in ${formFieldList.join(',')}`);
        // console.log(`Received ${formFieldList.indexOf(value.toString())} for ${key} in ${formFieldList.join(',')}`);
        if (value === 'draftId') {
          this.id = paramMap.get(value) ?? null;
          //console.log('Set id to ', this.id);
        } else if (formFieldList.indexOf(value) >= 0) {
          // console.log(`Found ${value} in element`);
          let newValue = paramMap.get(value);
          if (newValue == null) {
            return;
          }
          //console.log(`Found ${value} in element, setting to ${newValue}`);
          const ux = this.uxService.getUserExperience();
          const activeEmployee = ux?.activeEmployee;
          if (activeEmployee) {
            /* 
                            Not all Filter String replaces need to be here.
                            Only Route Url (page=>page) replacements need to be here
                        */
            // console.log(`[EBOEdit] Replacing <Self> values with current Profile values`);
            newValue = newValue.replace('<SELF>', activeEmployee.id);
            newValue = newValue.replace('<SELF.FedEx>', activeEmployee.fedExID ? activeEmployee.fedExID : 'null');
          }
          //console.log(`Setting ${value.toString()} = '${newValue}'`);
          const elementKey = value as keyof T & string;
          const attribute = this.schema.getAttribute(elementKey.toString());
          if (!attribute || !this.element) {
            return;
          }
          switch (attribute.displayType) {
            case DisplayType.Enum:
              this.element[elementKey] = parseInt(newValue, 10) as T[keyof T & string];
              break;
            case DisplayType.EnumArray:
              this.element[elementKey] = paramMap.getAll(value).map((val) => parseInt(val, 10)) as T[keyof T & string];
              break;
            case DisplayType.Boolean:
              this.element[elementKey] = (newValue === 'true' || newValue === '1') as T[keyof T & string];
              break;
            default:
              this.element[elementKey] = newValue as T[keyof T & string];
              break;
          }
        }
      });
    }
    // console.log('Parameters Updated: ', this.element);
  }

  activator<T>(item: Partial<T>, type: { new (item): T }): T {
    //let test: new(item) => T;
    return new type(item);
  }
  public processCrudResult(resultItem: CrudCacheItem<T>) {
    //console.log(`Processing crud result`, resultItem);
    if (!resultItem) return;
    this.element =
      resultItem.data && !Array.isArray(resultItem.data)
        ? this.schema.NewObject(resultItem.data)
        : this.schema.NewObject();
    //auto sort
    const childEditConfigs = this.childEditConfigs;
    if (childEditConfigs) {
      Object.keys(childEditConfigs).forEach((key) => {
        const childEditConfig = childEditConfigs[key as keyof T];
        //console.log(`Updating ${key} with`, this.element[key]);
        if (childEditConfig?.sortItemsCallback) {
          childEditConfig.sortItemsCallback();
        }
      });
    }
    //console.log(`Processing crud result: done`, this.element);
    // console.log('[SummitEditPrime] processCrudResult - Calling updateChildren...', { dataId: resultItem.dataId });
    this.updateChildren(resultItem.dataId);
    // console.log('[SummitEditPrime] processCrudResult - After updateChildren');
  }

  public updateChildren(updateId?: string) {
    // console.log('[SummitEditPrime] updateChildren - CALLED', {
    //   updateId,
    //   stack: new Error().stack?.split('\n').slice(1, 5).join('\n')
    // });
    // Debounce to prevent excessive calls during initialization
    if (this.updateChildrenTimer) {
      clearTimeout(this.updateChildrenTimer);
    }

    this.updateChildrenTimer = setTimeout(() => {
      updateId ??= uuid();
      const childEditConfigs = this.childEditConfigs;
      if (childEditConfigs) {
        Object.keys(childEditConfigs).forEach((key) => {
          const childEditConfig = childEditConfigs[key as keyof T];
          //console.log(`Updating ${key} with`, this.element[key]);
          if (childEditConfig?.gridRef?.updateDataFromLocal) {
            childEditConfig.gridRef.updateDataFromLocal(this.element[key] as unknown[], updateId);
          }
        });
      }
      //cd was being run in constructor before child grids were initialized
      if (this.initialized) this.cd.detectChanges();
      this.updateChildrenTimer = null;
    }, this.updateChildrenDebounceMs);
  }

  protected updateForm(): void {
    const newValues: Partial<T> = Object.assign({}, this.element);
    if (this.elementForm) this.elementForm.patchValue(newValues, { emitEvent: false });
    this.updateChildren();
  }

  protected override preSaveCheck(): boolean {
    this.showProgressSpinner(true, 'Saving');
    const result = super.preSaveCheck();
    if (result === false) {
      this.showProgressSpinner(false);
    }
    return result;
  }

  public onSave(autoRefresh = true, autoHistoryBack = true): void {
    //console.log('base on save');
    if (this.preSaveCheck() === false) {
      return;
    }
    //console.log(`Submitting form: Passed Pre-check`, this.elementForm.value);
    // ** Limit Element Form field to only Object values. **
    const updateData = this.schema.NewObject(this.elementForm?.value) as Partial<T>;
    // Remove Fields that are outside of Form
    if (this.fileUploadComponentName) delete updateData['files'];
    this.cleanFormObject(updateData);
    // ** Take Remaining Fields and copy into element **
    //console.log('Applying update data', updateData, this.element);
    this.element = assignSafe(this.element, updateData);
    this.cleanFormTypes(this.element);
    // ** Update any Non-Form Fields
    // Need to be updated in custom On Submit
    // this.element.SetFlag(CompanyFlags.ProvideAssets, this.elementForm.value.providesAssets);
    if (this.fileUploadComponentName) {
      //console.log('Submitting with files');
      const fileUploadComponent = this.fileUploadComponentName
        ? (this[this.fileUploadComponentName as keyof this] as SFormInputFileComponent<T>)
        : undefined;
      //console.log(`Submitting with files`, fileUploadComponent, this.element);
      if (fileUploadComponent) {
        //console.log(`Submitting with files`, this.element['files']);
        (this.element as unknown as IFileContainer).files = fileUploadComponent.fileRefs?.filter(
          (fileRef) => !fileRef.isLocal
        );
        //console.warn(`Submitting with files`, fileUploadComponent, autoRefresh, autoHistoryBack);
        this.submitToServerWithFiles(
          fileUploadComponent.files as unknown as FileList,
          fileUploadComponent.newFileRefs,
          autoRefresh,
          autoHistoryBack
        );
        return;
      } else {
        console.error(`File Upload Component ${this.fileUploadComponentName} not found`);
      }
    }
    //console.log('calling finalize');
    this.finalize(autoRefresh, autoHistoryBack);
  }

  protected cleanFormObject(updateData: Partial<T>): void {
    if (this.childEditConfigs) {
      Object.keys(this.childEditConfigs).forEach((key) => {
        try {
          delete updateData[key];
        } catch (e) {
          //NOP
        }
      });
    }
  }
  protected cleanFormTypes(updateData?: Partial<T>): Partial<T> | undefined {
    if (!updateData) return undefined;
    Object.keys(updateData).forEach((key) => {
      const attribute = this.schema.getAttribute(key as keyof T & string);
      if (attribute?.displayType) {
        switch (attribute.displayType) {
          case DisplayType.Financial:
          case DisplayType.Money:
            if (isString(updateData[key])) {
              const cleanValue = updateData[key].toString().replace(',', '');
              updateData[key as keyof T] = parseFloat(cleanValue) as T[keyof T];
            }
            break;
          default:
            break;
        }
      }
    });
    return updateData;
  }

  protected UpdateTitle() {
    if (this.debug) console.log(`EBOEdit: UpdateTitle`);
    if (this.newEntry) {
      // console.log('Updating Title to New');
      this.crud.updateTitle('Entry', 'New');
      return;
    }
    const newName = this.element?.['toShortString'] ? (this.element as unknown as SBaseDisplay).toShortString() : null;
    if (newName) {
      // console.log('Updating Title to short string', this.element);
      this.crud.updateTitle('Entry', newName);
      return;
    }
    // console.log('Updating Title to Loading');
    this.crud.updateTitle('Entry', '[Loading]');
  }

  protected finalize(refreshUx = true, autoHistoryBack?: boolean) {
    autoHistoryBack ??= true;
    this.submittingBs.next(true);
    if (this.debug) console.log(`[Summit Edit] cleaning`, this.element);
    const refServerApi = this.schema.refServerAPI;
    const lclElement = cloneDeep(this.element);
    //if (this.debug) console.log(`[Summit Edit] submitToServer`, this.schema, this.newEntry);
    const cleanElement = this.schema.Clean(cloneDeep(lclElement));
    if (this.dialogRef && this.dialogConfig?.data?.autoSave !== true) {
      //console.log(`[Summit Edit] Returning to Parent`, lclElement, cleanElement);
      this.returnToParent(lclElement, cleanElement);
    } else if (refServerApi) {
      if (testCreationMode) console.log(`Test Creation Client  Request:\n ${JSON.stringify(cleanElement, null, 2)}`);
      if (this.newEntry) {
        //console.log('[Summit Edit] Creating', cleanElement);
        //console.log(`[Summit Edit] Creating via ${this.schema.refServerAPI}`, JSON.stringify(cleanElement));
        this.crud
          .create(refServerApi, cleanElement.id?.toString() ?? 'create', cleanElement)
          .pipe(
            catchError((err) => this.handleCrudError(err)),
            takeUntil(this.disposing$)
          )
          .subscribe((result) => {
            if (result != 'error') {
              if (this.dialogRef) {
                this.submittingBs.next(false);
                this.returnToParent(lclElement, cleanElement);
              } else {
                this.saveComplete(result, refreshUx, autoHistoryBack);
                this.submittingBs.next(false);
              }
            }
            this.submittingBs.next(false);
          });
      } else {
        //console.log('[EboEdit] Updating', cleanElement);
        // console.log('Updating\n', JSON.stringify(this.element));
        const cleanId = cleanElement.id;
        if (cleanId == null) {
          console.error(`Cannot update without id`);
          this.submittingBs.next(false);
          return;
        }
        this.crud
          .edit(refServerApi, this.getRequiredId(cleanElement.id, 'edit'), cleanElement)
          .pipe(
            catchError((err) => this.handleCrudError(err)),
            takeUntil(this.disposing$)
          )
          .subscribe((result) => {
            //console.log(`[Summit Edit] Edit Result`, result);
            if (result != 'error') {
              if (this.dialogRef) {
                this.submittingBs.next(false);
                this.returnToParent(lclElement, cleanElement);
              } else {
                this.saveComplete(result, refreshUx, autoHistoryBack);
                this.submittingBs.next(false);
              }
            }
            this.submittingBs.next(false);
          });
      }
    }
  }

  protected handleCrudError(err: unknown) {
    this.apiError = ApiError.ProcessServerError(err, this.elementForm);
    this.submittingBs.next(false);
    return of('error');
  }

  protected async submitToServerWithFiles(
    files: FileList,
    fileInfo: IFileReference[],
    refreshUx?: boolean,
    autoHistoryBack?: boolean
  ): Promise<void> {
    //console.warn(`[Summit Edit] submitToServerWithFiles`, files, fileInfo, refreshUx, autoHistoryBack);
    refreshUx ??= true;
    autoHistoryBack ??= true;
    //console.log(`[Summit Edit] submitToServer`, fileInfo);
    this.submittingBs.next(true);
    const refServerApi = this.schema.refServerAPI;
    const lclElement = cloneDeep(this.element);
    const cleanElement = this.schema.Clean(lclElement);
    if (testCreationMode) console.log(`Test Creation Client  Request:\n ${JSON.stringify(cleanElement, null, 2)}`);

    const lclFormData = new FormData();
    //console.log(`sending \n ${JSON.stringify(cleanElement)}`);
    lclFormData.append('data', JSON.stringify(cleanElement));
    //console.log(`[Summit Edit] Creating via form`, lclFormData, cleanElement);
    const lclFileInfo: Array<{ id?: string | number | null; name: string; contentType: FileContentType }> = [];
    if (files) {
      //console.log(`[Summit Edit] Appending ${files.length} files`);
      for (let i = 0; i < files.length; i++) {
        let fileToAppend = files[i];
        if (fileToAppend.type === 'image/jpeg' || fileToAppend.type === 'image/png') {
          // console.log(
          //   `Processing file ${fileToAppend.name} of type ${fileToAppend.type} with size ${fileToAppend.size} bytes`,
          //   cloneDeep(fileToAppend)
          // );
          fileToAppend = await this.resizeImageFile(fileToAppend, 2048);
          // console.log(`Resized file ${fileToAppend.name} to size ${fileToAppend.size} bytes`, fileToAppend);
        }
        lclFormData.append('files', fileToAppend);
        const fileRef = fileInfo.find((file) => file.name === files[i].name);
        const selectedContentType = fileRef?.contentType ?? FileContentType.Attachment;
        lclFileInfo.push({ id: fileRef?.['id'], name: files[i].name, contentType: selectedContentType });
      }
    }
    lclFormData.append('fileInfo', JSON.stringify(lclFileInfo));

    if (this.dialogRef && this.dialogConfig?.data?.autoSave !== true) {
      if (this.dialogConfig?.data?.autoClean === false) {
        this.dialogRef.close(lclElement);
      } else {
        this.dialogRef.close(cleanElement);
      }
    } else if (refServerApi) {
      if (this.newEntry) {
        //console.log('[Summit Edit] Creating via form', lclFormData.getAll('data'));
        //console.log(`[Summit Edit] Creating via ${this.schema.refServerAPI}`, JSON.stringify(cleanElement));
        this.crud
          .create(refServerApi, cleanElement.id?.toString() ?? 'create', lclFormData, {})
          .pipe(
            catchError((err) => this.handleCrudError(err)),
            takeUntil(this.disposing$)
          )
          .subscribe((result) => {
            if (result != 'error') {
              if (this.dialogRef) {
                this.submittingBs.next(false);
                this.returnToParent(lclElement, cleanElement);
              } else {
                this.saveComplete(result, refreshUx, autoHistoryBack);
                this.submittingBs.next(false);
              }
            }
            this.submittingBs.next(false);
          });
      } else {
        //console.log('[EboEdit] Updating', this.element);
        //console.log('Updating with form data', lclFormData);
        //console.log('Updating\n', JSON.stringify(this.element));
        if (lclElement.id == null) {
          console.error(`Cannot update without id`);
          this.submittingBs.next(false);
          return;
        }
        this.crud
          .edit(refServerApi, this.getRequiredId(lclElement.id, 'edit'), lclFormData, {})
          .pipe(
            catchError((err) => this.handleCrudError(err)),
            takeUntil(this.disposing$)
          )
          .subscribe((result) => {
            //console.log(`[Summit Edit] Edit Result`, result);
            if (result != 'error') {
              if (this.dialogRef) {
                this.submittingBs.next(false);
                this.returnToParent(lclElement, cleanElement);
              } else {
                this.saveComplete(result, refreshUx, autoHistoryBack);
                this.submittingBs.next(false);
              }
            }
            this.submittingBs.next(false);
          });
      }
    }
  }

  showProgressSpinner(show: boolean, text?: string) {
    if (!this.inDialog) this.notificationService.showProgressSpinner(show, text);
  }

  returnToParent(lclElement: T, cleanElement: Partial<T>) {
    //console.log(`Returning to Parent`, lclElement, cleanElement);
    if (this.dialogRef) {
      if (this.dialogConfig?.data?.autoClean === false) {
        //console.log('returning', this.element);
        this.dialogRef.close(lclElement);
      } else {
        //console.log('returning [clean]', this.element);
        this.dialogRef.close(cleanElement);
      }
    }
  }
  async saveComplete(_data: unknown, refreshUx: boolean, autoHistoryBack?: boolean, navigateOverride?: string) {
    autoHistoryBack ??= true;
    //console.log(`Save complete, cleaning up form and refreshUx: ${refreshUx} & autoHistoryBack: ${autoHistoryBack}`);
    this.nonFormDataDirty = false;
    this.elementForm.markAsPristine();
    if (this.crud && autoHistoryBack) {
      //console.log(`Clearing Crud for ${this.schema.refServerAPI}/${this.id}`);
      this.crud.clear(this.schema, this.id?.toString());
      if (this.schema.refServerAPI) {
        this.crud.markStale(this.schema.refServerAPI);
      }
    }
    if (refreshUx && this.crud) {
      //console.log(`Refreshing Crud for ${this.schema.refServerAPI}`);
      await this.crud.refresh(this.schema);
    }
    //console.log(`Calling history.back`);
    this.openSaveToast();
    if (navigateOverride) {
      //console.log(`Navigating to override ${navigateOverride}`);
      this.router.navigateByUrl(navigateOverride);
      return;
    }
    if (autoHistoryBack) {
      //console.log(`Navigating to back`);
      if (this.dialogRef) {
        this.submittingBs.next(false);
        this.dialogRef.close();
      } else {
        this.history.back();
      }
    }
    this.submittingBs.next(false);
  }

  getNewEntryEventDate(): Dayjs {
    let response: Dayjs | undefined;
    const queryParams = this.activeRoute?.snapshot.queryParamMap;
    //console.log(`DateRange`, queryParams?.params.DateRange)
    const dateRangeRaw = queryParams?.get('DateRange');
    if (dateRangeRaw) {
      console.log(`parsing: ${dateRangeRaw}`);
      const dateRange = new DateRange(JSON.parse(dateRangeRaw));
      //console.log(`Parsed ${dateRange.begin.format()}-${dateRange.end.format()}`);
      const nowMoment = SDate.newUtc();
      if (nowMoment.isSameOrAfter(dateRange.begin)) {
        if (nowMoment.isSameOrBefore(dateRange.end)) {
          response = SDate.getNormalized();
        } else {
          response = dateRange.end;
        }
      } else {
        response = dateRange.begin;
      }
    }
    return response ?? SDate.getNormalized();
  }

  refreshData() {
    this.crud.refresh(this.schema, this.id?.toString() ?? null);
  }

  override ngOnDestroy(): void {
    this.loadingBs.next(false);
    this.loadingBs.complete();
    this.submittingBs.next(false);
    this.submittingBs.complete();
    super.ngOnDestroy();
  }

  addChildItem<U extends SummitIdObject>(
    property: keyof T,
    $event: ISummitGroupEvent<U> | ISummitItemEvent<U> | Partial<U>
  ) {
    //console.log(`Adding child item to ${property.toString()}`, $event);
    const childConfig = this.childEditConfigs?.[property];
    if (!childConfig) {
      console.error(`No child config for ${property.toString()}`);
      return;
    }
    if (childConfig.preAddCallback) {
      childConfig.preAddCallback();
    }
    let combinedDefault: Partial<U> = {};
    if (childConfig.getDefaultValue) combinedDefault = assignSafe(combinedDefault, childConfig.getDefaultValue());
    if ('defaultObject' in $event && $event.defaultObject) {
      combinedDefault = assignSafe(combinedDefault, $event.defaultObject);
    } else {
      combinedDefault = assignSafe(combinedDefault, $event);
    }

    if (!childConfig.childType) {
      this.launchChildDialog(property, combinedDefault as U);
      return;
    }

    try {
      const child = new childConfig.childType(combinedDefault);
      this.launchChildDialog(property, child);
    } catch (_e) {
      this.launchChildDialog(property, combinedDefault as U); // item is enum and not an object
    }
  }

  editChildItem(property: keyof T, id: string) {
    //console.log(`Editing child item ${id} from ${property.toString()}`);
    const childConfig = this.childEditConfigs?.[property];
    if (!childConfig) {
      console.error(`No child config for ${property.toString()}`);
      return;
    }
    const childItems = this.element[property] as Array<SummitIdObject>;
    const editIndex = Array.isArray(childItems) ? (childItems?.findIndex((entry) => entry.id === id) ?? -1) : -1;
    if (editIndex < 0) return;
    const editItem = this.element[property][editIndex];
    this.launchChildDialog(property, editItem, editIndex);
  }
  copyChildItem<U extends SummitIdObject>(property: keyof T, id: string) {
    //console.log(`Copying child item ${id} from ${property.toString()}`);
    const childConfig = this.childEditConfigs?.[property];
    if (!childConfig) {
      console.error(`No child config for ${property.toString()}`);
      return;
    }
    const childItems = this.element[property] as Array<SummitIdObject>;
    const editItem = childItems.findIndex((entry) => entry.id === id);
    if (editItem < 0) return;
    if (!childConfig.childType) {
      console.error(`No child type config for ${property.toString()}`);
      return;
    }
    let newItem = new childConfig.childType(JSON.parse(JSON.stringify(childItems[editItem])));
    if (childConfig.getCopyOverlay) {
      const editOverlay = childConfig.getCopyOverlay();
      if (editOverlay === true) {
        if (childConfig.getDefaultValue) newItem = assignSafe(newItem, childConfig.getDefaultValue());
      } else newItem = assignSafe(newItem, childConfig.getCopyOverlay());
    }
    newItem['id'] = uuid();
    //console.log(`New item:`, newItem);
    this.launchChildDialog(property, newItem);
  }

  launchChildDialog<U>(property: keyof T, item: U, index?: number) {
    const dialogConfig = {} as DynamicDialogConfig;
    const childConfig = this.childEditConfigs?.[property];
    if (!childConfig) {
      console.error(`No child config for ${property.toString()}`);
      return;
    }
    //console.log(`Launching Child Dialog`, childConfig, item);
    if (childConfig.disableAutoZIndex) {
      dialogConfig.baseZIndex = 2101;
      dialogConfig.autoZIndex = false; //disabled due to time picker being put behind dialog
      dialogConfig.styleClass = 'parent-z-index-3';
    }
    dialogConfig.header = childConfig.title;
    dialogConfig.dismissableMask = true;
    //console.log(`[SummitEdit] Launching Child Dialog`, dialogConfig);
    dialogConfig.data = new DialogEditConfig<U>({
      element: item,
      hideFields: childConfig.hideFields as (keyof U & string)[],
      relatedElements: childConfig?.getRelatedElements ? childConfig.getRelatedElements() : null,
      autoClean: childConfig.autoClean,
      selectedTab: childConfig.getActiveTab
        ? (childConfig.getActiveTab(item) ?? childConfig.selectedTab)
        : childConfig.selectedTab
    });
    if (childConfig.getFilter) dialogConfig.data.filter = childConfig.getFilter();
    if (!childConfig.component) return;
    this.sDialog.show(childConfig.component, dialogConfig, (result) => {
      this.nonFormDataDirty = true;
      //console.log(`Got result from child dialog: `, result);
      //console.log(`Looking at Parent: `, childConfig);
      this.element[property] ??= [] as T[keyof T];
      let childItems = this.element[property];
      if (childConfig.processResultsCallback) {
        //console.log(`Calling Parent: `, childConfig);
        const newValue = childConfig.processResultsCallback(result as Partial<unknown>) as U | U[];
        //console.log(`Calling Parent Returned: `, newValue);
        if (Array.isArray(childItems)) {
          if (index != null && index != undefined) {
            childItems[index] = newValue as U;
          } else {
            if (Array.isArray(newValue)) {
              // console.log('update childItems');
              childItems.push(...newValue);
              // console.log('update childItems', childItems);
            } else {
              //console.log(`Adding:`, newValue, this.element[property]);
              childItems.push(newValue);
            }
          }
        } else {
          if (isNumber(this.element[property])) {
            //used for single object like bitwise flags
            const newFlags = newValue as number;
            this.element[property] = ((this.element[property] as number) | newFlags) as unknown as T[keyof T];
            childItems = this.element[property]; //reset childItems to new value
            //console.log(`New flags:`, this.element[property]);
          }
        }
      }
      if (childConfig.sortItemsCallback) childConfig.sortItemsCallback();
      //console.log(`Updating Table:`, childConfig);
      if (childConfig.gridRef?.updateDataFromLocal && (Array.isArray(childItems) || isNumber(childItems))) {
        childConfig.gridRef.updateDataFromLocal(childItems, uuid());
      }
      if (childConfig.postUpdate) childConfig.postUpdate();
    });
  }

  batchChangeChildItem<U>(property: keyof T, batchChange: BatchChange<U>) {
    //console.log(`Applying Batch Change`, batchChange);
    const childConfig = this.childEditConfigs?.[property];
    if (!childConfig) {
      console.error(`No child config for ${property.toString()}`);
      return;
    }
    const childItems = this.element[property] as Array<SummitIdObject>;
    batchChange.applyTo(childItems);
    if (childConfig.gridRef) {
      childConfig.gridRef.updateDataFromLocal(childItems, uuid());
    }
    if (childConfig.postUpdate) childConfig.postUpdate();
  }

  removeChildItem(property: keyof T, id: string) {
    //console.log(`Removing ${id} from ${property.toString()}`);
    const childConfig = this.childEditConfigs?.[property];
    if (!childConfig) {
      console.error(`No child config for ${property.toString()}`);
      return;
    }
    let childItems = this.element[property] as Array<SummitIdObject> | number;
    if (Array.isArray(childItems)) {
      const removeIndex = childItems.findIndex((entry) => entry.id === id);
      if (removeIndex < 0) return;
      //console.log(`Removing ${id} at ${removeIndex} from ${property.toString()}`, [...childItems]);
      childItems.splice(removeIndex, 1);
    } else if (isNumber(this.element[property])) {
      //console.log(`Removing ${id} from ${property.toString()}`);
      //used for single object like bitwise flags
      this.element[property] = ((this.element[property] as number) & ~parseInt(id)) as unknown as T[keyof T];
      childItems = this.element[property] as number; //reset childItems to new value
      //console.log(`New flags:`, this.element[property]);
    }
    if (childConfig.gridRef) childConfig.gridRef.updateDataFromLocal(childItems, uuid());
    this.nonFormDataDirty = true;
    if (childConfig.postUpdate) childConfig.postUpdate();
  }

  close($event?: Event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.history.back();
    }
  }

  async resizeImageFile(file: File, maxSize = 2048): Promise<File> {
    // console.log(`Resizing image file: ${file.name} (${file.size} bytes)`, file.bytes);
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > maxSize || height > maxSize) {
            const aspect = width / height;
            if (aspect > 1) {
              width = maxSize;
              height = Math.round(maxSize / aspect);
            } else {
              height = maxSize;
              width = Math.round(maxSize * aspect);
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              const resizedFile = new File([blob], file.name, { type: file.type });
              resolve(resizedFile);
            },
            file.type,
            0.95
          );
        };
        // console.log(`Resizing image file: ${file.name} (${file.size} bytes)`, event);
        const imgSrc = event.target?.result;
        if (!imgSrc || typeof imgSrc !== 'string') {
          resolve(file);
          return;
        }
        img.src = imgSrc;
      };
      reader.readAsDataURL(file);
    });
  }

  private getRequiredId(id: string | number | null | undefined, action: 'create' | 'edit'): string {
    if (id == null) {
      return action === 'create' ? 'create' : '';
    }

    return id.toString();
  }
}
